// Export a dashboard to PNG / PDF for meetings.
// PNG  = one capture of the whole content-sized report.
// PDF  = capture the header + each card separately, then pack them across as
//        many A4 pages as needed. Charts stay whole in two columns; long prose
//        reflows across the page and splits between text lines.
// html-to-image handles recharts SVG more reliably than html2canvas; we await
// document.fonts.ready so text isn't clipped, and paint on the app's paper colour.

import { TENANT } from '../tenant'
import type { Chart } from '../types'
import { changeDirection, formatDay, formatPercent, formatShare, todayIso } from '../format'
import { buildTrendCsv } from './csv'
import { pdfLines, pdfText } from './pdfText'
import { UNNAMED_ARTICLE_NOTE } from '../missingLabels'
import type { ExportSelection } from './exportSelection'

const PAPER = '#f4f9f8' // index.css --bg
const MUTED = '#5d6f70' // --grey-text

// #10 Excel-export of a trend. CSV is dependency-free and opens natively in
// Excel; we use ';' as separator and ',' as decimal (Swedish locale) and prepend
// a UTF-8 BOM so åäö and headers survive. Multi-series charts get one column per
// series. No number is reformatted with thousands separators (Excel must parse it).
// `filename` is already a complete, filesystem-safe name (buildExportFilename,
// MP-OS-190) — the caller owns the standardised report-type/scope/period/segment
// pattern, so this does not re-slug it.
export function exportTrendCsv(chart: Chart, filename: string, selection?: ExportSelection): void {
  const blob = new Blob([buildTrendCsv(chart, selection)], { type: 'text/csv;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

interface Shot {
  url: string
  w: number
  h: number
  paginate?: boolean
  pageBreakBefore?: boolean
}

// MPD-64: a file cannot open an ⓘ, so exports keep the explanation written out. While a node is
// captured it carries this attribute; App.css then hides every ⓘ button inside it and writes each
// tip's text after its label (an InfoTip's text is always in the DOM). Removed afterwards, also
// when the capture fails.
export const EXPORTING_ATTR = 'data-exporting'

// MPD-76 F6: captures of one node can overlap (an aborted run's capture still finishing while the
// retry captures), so the attribute counts them and only the last one to finish removes it.
const activeCaptures = new WeakMap<HTMLElement, number>()

async function shoot(node: HTMLElement): Promise<Shot> {
  const { toPng } = await import('html-to-image')
  activeCaptures.set(node, (activeCaptures.get(node) ?? 0) + 1)
  node.setAttribute(EXPORTING_ATTR, '')
  let url: string
  try {
    url = await toPng(node, { backgroundColor: PAPER, pixelRatio: 2, cacheBust: true })
  } finally {
    const left = (activeCaptures.get(node) ?? 1) - 1
    if (left > 0) activeCaptures.set(node, left)
    else {
      activeCaptures.delete(node)
      node.removeAttribute(EXPORTING_ATTR)
    }
  }
  const img = new Image()
  img.src = url
  await img.decode()
  return { url, w: img.naturalWidth, h: img.naturalHeight }
}

// Paginate actual text BEFORE rasterisation: bounded, complete page-sized nodes
// retain Unicode without shrinking the text or relying on PDF's WinAnsi font.
async function proseShots(el: HTMLElement, page: HTMLElement, width: number,
  firstHeight: number, pageHeight: number): Promise<Shot[]> {
  const text = el.dataset.exportKind === 'action'
    ? Array.from(el.querySelectorAll('li'), item => `• ${item.textContent ?? ''}`).join('\n')
    : el.querySelector('.export-text')?.textContent ?? ''
  const graphemes = Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text), part => part.segment)
  const shots: Shot[] = []
  let start = 0
  do {
    const node = el.cloneNode(false) as HTMLElement
    Object.assign(node.style, { width: `${width}px`, position: 'static', height: 'auto', fontFamily: 'Arial, sans-serif' })
    const heading = el.querySelector('h3')?.cloneNode(true) as HTMLElement | undefined
    if (heading && !start) {
      heading.style.fontFamily = 'Arial, sans-serif'
      node.appendChild(heading)
    }
    const body = document.createElement('p')
    body.className = 'export-text'
    body.style.fontFamily = 'Arial, sans-serif'
    body.style.overflowWrap = 'anywhere'
    node.appendChild(body)
    page.appendChild(node)
    try {
      const maximum = shots.length ? pageHeight : firstHeight
      let low = start, high = graphemes.length
      // Binary search measured layout, not a guessed character/font-size limit.
      while (low < high) {
        const candidate = Math.ceil((low + high) / 2)
        body.textContent = graphemes.slice(start, candidate).join('')
        if (node.getBoundingClientRect().height <= maximum) low = candidate
        else high = candidate - 1
      }
      if (low === start && start < graphemes.length) throw new Error('The text heading is too tall for a PDF page.')
      let end = low
      // Prefer a word boundary; an oversized unbroken word still advances by
      // whole graphemes, never splitting an emoji or combining character.
      if (end < graphemes.length) {
        while (end > start && !/\s/u.test(graphemes[end - 1])) end -= 1
        if (end === start) end = low
      }
      body.textContent = graphemes.slice(start, end).join('')
      shots.push({ ...await shoot(node), paginate: true })
      start = end
    } finally { node.remove() }
  } while (start < graphemes.length)
  return shots
}

// #13 Månadsrapport — a one-click PDF a sales lead can take to a meeting. Built
// programmatically (deterministic tables, always renders) rather than by DOM
// capture; the trend chart is embedded as an image only when its node captures.
export interface MonthlyReport {
  manufacturer: string
  periodLabel: string
  previousPeriodLabel: string | null
  cuDeltaAbs: number | null
  cuDeltaPct: number | null
  dosor: number
  shareLabel: string
  sharePct: number | null
  // MPD-68: the segment the share and the brand table are for — the supplier's main segment,
  // All White for the whole market; null when the supplier has no sales in any segment.
  segment: string | null
  marketSegment: number | null
  // MPD-73: what the segment market covers, in the share card's wording (segmentMarketScope):
  // a whole segment, or only the supplier's product types for Vape/Smoking. null with no segment.
  marketScope: { short: string; narrowed: boolean } | null
  reconciliation: string
  trendCaption: string
  // MPD-133 L6: the line printed in the trend's place when the report's trend request failed or
  // returned nothing (monthlyReportData.REPORT_TREND_UNAVAILABLE_PDF); null with a trend.
  trendNote?: string | null
  shareTable: { brand: string; pct: number; own?: boolean }[] // own: the supplier's own brand (MP-OS-192)
  // unnamed (MPD-185): the article has no name ("Unnamed article (<sku>)"); its ⓘ prints as a note.
  topSkus: { label: string; dosor: number; unnamed?: boolean }[]
}

// The scorecard's change line. ASCII-only signs: jsPDF's standard helvetica is WinAnsi-encoded
// and cannot render ▲/▼ or the U+2212 minus. MP-OS-206: the percent's sign follows the displayed
// percent (changeDirection), so a change that rounds to 0.0 % prints "0.0 %" with no sign; the
// CU figure keeps its own sign. MPD-115 #7: the digits come from formatPercent, the screen's
// Intl rounding (1.15 prints 1.2, where toFixed gave 1.1), with its NBSP before "%".
export function monthlyDeltaNote(r: Pick<MonthlyReport, 'cuDeltaAbs' | 'cuDeltaPct' | 'previousPeriodLabel'>): string {
  if (r.cuDeltaAbs == null || r.cuDeltaPct == null) return 'No comparable period available.'
  const dir = changeDirection(r.cuDeltaPct)
  if (dir === 'none') return 'No comparable period available.'
  const pct = dir === 'flat' ? formatPercent(0) : `${dir === 'up' ? '+' : '-'}${formatPercent(Math.abs(r.cuDeltaPct))}`
  const cu = Math.round(r.cuDeltaAbs)
  const abs = `${cu > 0 ? '+' : cu < 0 ? '-' : ''}${Math.abs(cu).toLocaleString('sv-SE')}`
  return `${abs} CU (${pct}) vs ${r.previousPeriodLabel}`
}

const INK = '#15302f'
const TEAL = '#0f4c52'

// `filename` comes from buildExportFilename (MP-OS-190), like every export here.
// `signal` (MP-OS-190) lets a caller abandon a run that already timed out in the UI —
// checked after each await so a stale capture never calls pdf.save() once the caller has
// moved on, which would otherwise download a report after the button already showed an
// error, or download twice when the user retries.
export async function exportMonthlyReport(r: MonthlyReport, chartNode: HTMLElement | null,
  filename: string, signal?: AbortSignal): Promise<void> {
  const { jsPDF } = await import('jspdf')
  await document.fonts.ready
  if (signal?.aborted) return
  const chart = chartNode ? await shoot(chartNode).catch(() => null) : null
  if (signal?.aborted) return

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const M = 44
  const right = pageW - M
  const fmt = (n: number) => Math.round(n).toLocaleString('sv-SE')
  let y = M + 6
  const ensureSpace = (height: number) => {
    if (y + height > pageH - M) {
      pdf.addPage()
      y = M
    }
  }
  const paragraph = (lines: string[], lineHeight: number) => {
    for (const line of lines) {
      ensureSpace(lineHeight)
      pdfText(pdf, line, M, y)
      y += lineHeight
    }
  }

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(22)
  pdf.setTextColor(TEAL)
  pdfText(pdf, 'Monthly report', M, y)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(MUTED)
  y += 18
  paragraph(pdfLines(pdf, `${r.manufacturer} · ${r.periodLabel}`, right - M), 15)
  y += 7
  pdf.setDrawColor(TEAL)
  pdf.setLineWidth(1.5)
  pdf.line(M, y, M + 60, y)
  y += 26

  const row = (label: string, value: string, note: string) => {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(11)
    const labels = pdfLines(pdf, label, right - M - 120)
    const notes = note ? pdfLines(pdf, note, right - M) : []
    ensureSpace(labels.length * 13 + notes.length * 11 + 9)
    pdf.setTextColor(MUTED)
    pdfText(pdf, labels, M, y)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(13)
    pdf.setTextColor(INK)
    pdfText(pdf, value, right, y, { align: 'right' })
    y += Math.max(13, labels.length * 13)
    if (notes.length) {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8.5)
      pdf.setTextColor(MUTED)
      for (const line of notes) {
        pdfText(pdf, line, right, y, { align: 'right' })
        y += 11
      }
    }
    y += 9
    pdf.setDrawColor('#e3eceb')
    pdf.setLineWidth(0.5)
    pdf.line(M, y - 12, right, y - 12)
  }

  const section = (t: string) => {
    ensureSpace(36)
    y += 8
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(12)
    pdf.setTextColor(TEAL)
    pdfText(pdf, t, M, y)
    y += 14
  }

  section('Scorecard')
  row('CU sold', fmt(r.dosor), monthlyDeltaNote(r))
  if (r.sharePct != null) row(r.shareLabel, formatShare(r.sharePct, { hasSales: true }), 'CU')
  if (r.segment) row(`Market · ${r.segment} · CU`, r.marketSegment != null ? fmt(r.marketSegment) : 'n.a.', r.marketScope?.short ?? '')
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(MUTED)
  const reconciliation = pdfLines(pdf, r.reconciliation, right - M)
  paragraph(reconciliation, 11)
  y += 8

  // MP-OS-192: the ranking names its month, and the supplier's own brand is marked, so its row
  // (for a single-brand supplier the same figure as the share above) reads as the same number.
  // A narrowed market says so in the heading too, so the ranking is not read as the whole segment.
  if (r.segment) section(`Share of ${r.segment} (CU)${r.marketScope?.narrowed ? `, ${r.marketScope.short}` : ''} · ${r.periodLabel}`)
  for (const s of r.segment ? r.shareTable.slice(0, 6) : []) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(11)
    const label = pdfLines(pdf, s.own ? `${s.brand} (your brand)` : s.brand, right - M - 90)
    ensureSpace(Math.max(18, label.length * 13))
    pdf.setTextColor(INK)
    pdfText(pdf, label, M, y)
    pdf.setTextColor(MUTED)
    pdfText(pdf, formatShare(s.pct, { hasSales: true }), right, y, { align: 'right' })
    y += Math.max(18, label.length * 13)
  }

  if (r.topSkus.length) {
    section(`Top articles (CU) · ${r.periodLabel}`)
    for (const s of r.topSkus.slice(0, 6)) {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(11)
      pdf.setTextColor(INK)
      const label = pdfLines(pdf, s.label, right - M - 90)
      ensureSpace(Math.max(18, label.length * 13))
      pdfText(pdf, label, M, y)
      pdf.setTextColor(MUTED)
      pdfText(pdf, fmt(s.dosor), right, y, { align: 'right' })
      y += Math.max(18, label.length * 13)
    }
    if (r.topSkus.slice(0, 6).some((s) => s.unnamed)) {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(9)
      pdf.setTextColor(MUTED)
      paragraph(pdfLines(pdf, UNNAMED_ARTICLE_NOTE, right - M), 11)
    }
  }

  if (chart) {
    pdf.setFontSize(8.5)
    const caption = pdfLines(pdf, r.trendCaption, right - M)
    const naturalHeight = chart.h * ((right - M) / chart.w)
    if (pageH - y - M < 26 + caption.length * 10 + naturalHeight) {
      pdf.addPage()
      y = M
    }
    section('Trend')
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8.5)
    pdfText(pdf, caption, M, y)
    y += caption.length * 10 + 5
    const scale = Math.min((right - M) / chart.w, (pageH - M - y) / chart.h)
    pdf.addImage(chart.url, 'PNG', M, y, chart.w * scale, chart.h * scale)
  } else if (chartNode) {
    pdf.setFontSize(9)
    paragraph(pdfLines(pdf, 'The trend image could not be included. The figures above are unchanged.', right - M), 11)
  } else if (r.trendNote) {
    section('Trend')
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    paragraph(pdfLines(pdf, r.trendNote, right - M), 11)
  }

  const date = formatDay(todayIso(), { long: true })
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(8)
  pdf.setTextColor(MUTED)
  for (let p = 1; p <= pdf.getNumberOfPages(); p++) {
    pdf.setPage(p)
    pdfText(pdf, `${TENANT.productName} · ${date} · units labelled per row`, M, pageH - 22)
  }
  if (signal?.aborted) return
  pdf.save(filename)
}

// Dashboard PNG/PDF: `filename` comes from buildExportFilename; an aborted `signal` (the
// button's useDownload unmounted) stops the run before it downloads anything.
export async function exportPng(page: HTMLElement, filename: string, signal?: AbortSignal): Promise<void> {
  await document.fonts.ready
  if (signal?.aborted) return
  const { url } = await shoot(page)
  if (signal?.aborted) return
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
}

export async function exportPdf(page: HTMLElement, filename: string, signal?: AbortSignal): Promise<void> {
  const { jsPDF } = await import('jspdf')
  await document.fonts.ready
  const headerEl = page.querySelector<HTMLElement>('[data-export-header]')
  const cardEls = Array.from(page.querySelectorAll<HTMLElement>('[data-export-card]'))
  // MPD-76 F7: an aborted run (the board was switched or the view unmounted) stops capturing at
  // the next step, not only before save.
  if (signal?.aborted) return
  const header = headerEl ? await shoot(headerEl) : null
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4', compress: true })
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const M = 32
  const G = 14
  const footerY = pageH - 18
  const colW = (pageW - 2 * M - G) / 2
  const bottom = footerY - 10
  const date = formatDay(todayIso(), { long: true })

  let y = M
  if (header) {
    // Reserve room for content even when a very long dashboard title wraps.
    const headerScale = Math.min((pageW - 2 * M) / header.w, (bottom - M) / 4 / header.h)
    const hH = header.h * headerScale
    pdf.addImage(header.url, 'PNG', M, y, header.w * headerScale, hH)
    y += hH + G
  }

  const cards: Shot[] = []
  for (const el of cardEls) {
    if (signal?.aborted) return
    const prose = ['text', 'action'].includes(el.dataset.exportKind ?? '')
    const bounds = el.getBoundingClientRect()
    if (prose && bounds.height * (colW / bounds.width) > bottom - M) {
      const pageStyle = getComputedStyle(page)
      const width = page.getBoundingClientRect().width
        - (parseFloat(pageStyle.paddingLeft) || 0) - (parseFloat(pageStyle.paddingRight) || 0)
      const scale = (pageW - 2 * M) / width
      const firstHeight = (bottom - (cards.length ? M : y)) / scale - 1
      const parts = await proseShots(el, page, width, firstHeight, (bottom - M) / scale - 1)
      cards.push(...parts.map((part, index) => ({ ...part, pageBreakBefore: cards.length > 0 || index > 0 })))
    } else {
      cards.push(await shoot(el))
    }
  }

  // Charts stay whole; prose images contain complete, page-sized text blocks.
  for (let i = 0; i < cards.length;) {
    const a = cards[i]
    if (a.paginate) {
      const scale = (pageW - 2 * M) / a.w
      if (a.pageBreakBefore) { pdf.addPage(); y = M }
      pdf.addImage(a.url, 'PNG', M, y, pageW - 2 * M, a.h * scale)
      y += a.h * scale + G
      i += 1
      continue
    }
    const b = cards[i + 1]?.paginate ? undefined : cards[i + 1]
    const aH = a.h * (colW / a.w)
    const bH = b ? b.h * (colW / b.w) : 0
    const rowH = Math.max(aH, bH)
    // The first row is fitted below the header. Otherwise an oversized first
    // card creates a header-only page before the same card is scaled anyway.
    if (i > 0 && y + rowH > bottom) {
      pdf.addPage()
      y = M
    }
    // A long text/action card may be taller than an entire page. Preserve its
    // aspect ratio while fitting it above the footer instead of clipping it.
    const aScale = Math.min(colW / a.w, (bottom - y) / a.h)
    const bScale = b ? Math.min(colW / b.w, (bottom - y) / b.h) : 0
    pdf.addImage(a.url, 'PNG', M, y, a.w * aScale, a.h * aScale)
    if (b) pdf.addImage(b.url, 'PNG', M + colW + G, y, b.w * bScale, b.h * bScale)
    y += Math.max(a.h * aScale, b ? b.h * bScale : 0) + G
    i += b ? 2 : 1
  }

  const pages = pdf.getNumberOfPages()
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.setTextColor(MUTED)
    pdfText(pdf, `${TENANT.productName} · ${date}`, M, footerY)
    pdfText(pdf, `Page ${p} / ${pages}`, pageW - M, footerY, { align: 'right' })
  }
  if (signal?.aborted) return
  pdf.save(filename)
}
